import {
  PermissionCode,
  mayEditMaintenanceWorkOrder,
  mayStartMaintenanceWorkOrder,
} from '@gb-ticket/shared-types';
import { Link, useNavigate, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/use-page-title';
import { cn } from '@/lib/utils';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { MaintenanceDetailField } from '@/products/maintenance/features/maintenance-detail-field';
import { TechnicianNotesCard } from '@/products/maintenance/features/protocol/technician-notes-card';
import { MaintenanceStatusBadge } from '@/products/maintenance/features/maintenance-status-badge';
import { MaintenanceWorkOrderArchiveButton } from '@/products/maintenance/features/work-orders/maintenance-work-order-archive-actions';
import { MaintenanceWorkOrderActions } from '@/products/maintenance/features/work-orders/maintenance-work-order-actions';
import {
  useMaintenanceWorkOrder,
  useStartMaintenanceWorkOrder,
} from '@/products/maintenance/features/work-orders/use-maintenance-work-orders';
import {
  MAINTENANCE_PLANNING_PATH,
  MAINTENANCE_TRASH_PATH,
  maintenanceProtocolPath,
} from '@/products/maintenance/maintenance-paths';

export function MaintenanceWorkOrderDetailPage() {
  const { workOrderId = '' } = useParams();
  const navigate = useNavigate();
  const order = useMaintenanceWorkOrder(workOrderId);
  const me = useMe();
  const start = useStartMaintenanceWorkOrder(workOrderId);
  usePageTitle(order.data?.orderNumber ?? 'Auftrag');

  const isAssignee = order.data?.assignedToId === me.data?.id;
  const canStart =
    isAssignee &&
    order.data != null &&
    order.data.trashedAt == null &&
    mayStartMaintenanceWorkOrder(order.data.status) &&
    hasPermission(me.data, PermissionCode.MAINTENANCE_PROTOCOLS_WRITE_OWN);
  const canEditProtocol =
    isAssignee &&
    order.data != null &&
    order.data.trashedAt == null &&
    mayEditMaintenanceWorkOrder(order.data.status) &&
    hasPermission(me.data, PermissionCode.MAINTENANCE_PROTOCOLS_WRITE_OWN);
  const canManageOrders = hasPermission(me.data, PermissionCode.MAINTENANCE_ORDERS_MANAGE);

  const primaryAction = canStart ? (
    <Button
      type="button"
      className="min-h-11 w-full sm:w-auto"
      disabled={start.isPending}
      onClick={() => void start.mutateAsync()}
    >
      Wartung starten
    </Button>
  ) : canEditProtocol && order.data ? (
    <Button asChild className="min-h-11 w-full sm:w-auto">
      <Link to={maintenanceProtocolPath(order.data.protocol.id)}>Protokoll fortsetzen</Link>
    </Button>
  ) : null;

  return (
    <div className="space-y-6 pb-24 sm:pb-0">
      {order.isLoading ? <p className="text-sm text-muted-foreground">Lädt …</p> : null}
      {order.data ? (
        <>
          <PlatformPageHeader
            title={order.data.orderNumber}
            description={`${order.data.customer.name} · ${order.data.installation.location}`}
            action={
              primaryAction ? <div className="hidden sm:block">{primaryAction}</div> : undefined
            }
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Auftrag</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="min-w-0 space-y-1">
                <p className="text-xs text-muted-foreground">Status</p>
                <MaintenanceStatusBadge status={order.data.status} />
              </div>
              <MaintenanceDetailField label="Termin" value={order.data.scheduledFor ?? '—'} />
              <MaintenanceDetailField label="Kunde" value={order.data.customer.name} />
              <MaintenanceDetailField label="Projekt" value={order.data.project.name} />
              <MaintenanceDetailField
                label="Anlage"
                value={`${order.data.installation.installationNumber} – ${order.data.installation.name}`}
              />
              <div className="min-w-0 space-y-1">
                <p className="text-xs text-muted-foreground">Monteur</p>
                <PersonChip
                  className="text-sm font-medium"
                  displayName={order.data.assignedTo.displayName}
                  profilePhotoUrl={order.data.assignedTo.profilePhotoUrl}
                />
              </div>
              <MaintenanceDetailField
                label="Vorlage"
                value={`${order.data.templateName} v${order.data.templateVersionNumber}`}
              />
              <MaintenanceDetailField label="Ort" value={order.data.installation.location} />
            </CardContent>
          </Card>

          {order.data.trashedAt && !canManageOrders ? (
            <p className="text-sm text-muted-foreground">Dieser Auftrag liegt im Papierkorb.</p>
          ) : null}

          {canManageOrders || isAssignee ? (
            <div className="flex flex-wrap gap-2">
              {canManageOrders ? (
                <MaintenanceWorkOrderActions
                  order={order.data}
                  onTrashed={() => {
                    void navigate(MAINTENANCE_PLANNING_PATH);
                  }}
                  onPurged={() => {
                    void navigate(MAINTENANCE_TRASH_PATH);
                  }}
                />
              ) : null}
              {isAssignee ? <MaintenanceWorkOrderArchiveButton order={order.data} /> : null}
            </div>
          ) : null}

          <TechnicianNotesCard notes={order.data.protocol.generalNotes} />

          {order.data.instructions ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Hinweise</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">{order.data.instructions}</p>
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}

      {primaryAction ? (
        <div className="fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 border-t bg-background px-4 py-3 sm:hidden">
          <div className={cn('mx-auto w-full max-w-2xl')}>{primaryAction}</div>
        </div>
      ) : null}
    </div>
  );
}
